import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthPageLayout } from '../components/AuthPageLayout';
import { PageLoadingState } from '../components/LoadingSpinner';
import { supabase } from '../lib/supabaseClient';
import { fetchMe, getLastMeAuthFailureCode, invalidateMeCache } from '../lib/meClient';
import { resolvePostLoginPath, sanitizeReturnPath, storeReturnPath, takeReturnPath } from '../lib/postLoginRedirect';

type AuthState = 'login' | 'register' | 'verify' | 'forgot' | 'reset-sent';

function toGerman(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('invalid login credentials') || m.includes('invalid email or password'))
    return 'E-Mail-Adresse oder Passwort falsch.';
  if (m.includes('token has expired') || m.includes('token is invalid') || m.includes('otp_expired') || m.includes('otp expired'))
    return 'Der Code ist abgelaufen oder ungültig. Bitte fordere einen neuen Code an.';
  if (m.includes('email not confirmed'))
    return 'E-Mail-Adresse noch nicht bestätigt. Bitte prüfe deinen Posteingang.';
  if (m.includes('user already registered') || m.includes('already been registered'))
    return 'Diese E-Mail-Adresse ist bereits registriert. Bitte melde dich an.';
  if (m.includes('password should be at least') || m.includes('password must be at least'))
    return 'Das Passwort muss mindestens 8 Zeichen lang sein.';
  if (m.includes('unable to validate email') || m.includes('invalid email'))
    return 'Ungültiges E-Mail-Format.';
  if (m.includes('rate limit') || m.includes('over_email_send_rate_limit') || m.includes('too many requests'))
    return 'Zu viele Versuche. Bitte warte einen Moment.';
  if (m.includes('only request this once every 60 seconds') || m.includes('request this after'))
    return 'Bitte warte 60 Sekunden, bevor du erneut einen Code anforderst.';
  if (m.includes('signup') && m.includes('disabled'))
    return 'Registrierungen sind momentan deaktiviert.';
  if (m.includes('email link is invalid') || m.includes('link is invalid or has expired'))
    return 'Der Bestätigungslink ist abgelaufen. Bitte registriere dich erneut.';
  if (m.includes('auth session missing') || m.includes('no session'))
    return 'Keine aktive Sitzung. Bitte melde dich erneut an.';
  if (m.includes('user not found'))
    return 'Nutzer nicht gefunden.';
  if (m.includes('network') || m.includes('fetch'))
    return 'Netzwerkfehler. Bitte prüfe deine Internetverbindung.';
  return message;
}

type BackendLoginResult =
  | { kind: 'ok'; instanceId: string }
  | { kind: 'pending_approval' }
  | { kind: 'trial_ended'; message: string };

async function exchangeTokenWithBackend(accessToken: string): Promise<BackendLoginResult> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ access_token: accessToken }),
  });
  const payload = (await res.json().catch(() => null)) as
    | {
        activeInstance?: { id?: string };
        pending_approval?: boolean;
        error?: string;
        code?: string;
      }
    | null;
  if (res.status === 202 && payload?.pending_approval) {
    return { kind: 'pending_approval' };
  }
  if (payload?.code === 'trial_ended' || (res.status === 403 && payload?.error?.includes('Testzeitraum'))) {
    return {
      kind: 'trial_ended',
      message:
        payload?.error ??
        'Dein Testzeitraum ist abgelaufen. Bitte melde dich unter hallo@meisterpfad.de, um fortzufahren.',
    };
  }
  if (!res.ok) throw new Error(payload?.error ?? 'Login fehlgeschlagen');
  invalidateMeCache();
  return { kind: 'ok', instanceId: String(payload?.activeInstance?.id ?? '') };
}

type LoginPageProps = {
  initialAuthState?: Extract<AuthState, 'login' | 'register'>;
};

export function LoginPage({ initialAuthState = 'login' }: LoginPageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [authState, setAuthState] = React.useState<AuthState>(initialAuthState);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [fullName, setFullName] = React.useState('');
  const [otpCode, setOtpCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [trialEnded, setTrialEnded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [sessionChecked, setSessionChecked] = React.useState(false);

  const queryNext = searchParams.get('next');

  const withNext = React.useCallback(
    (path: string) => {
      const next = sanitizeReturnPath(queryNext);
      if (!next) return path;
      const sep = path.includes('?') ? '&' : '?';
      return `${path}${sep}next=${encodeURIComponent(next)}`;
    },
    [queryNext],
  );

  const goAfterLogin = React.useCallback(
    (instanceId: string) => {
      navigate(resolvePostLoginPath(instanceId, takeReturnPath(queryNext)), { replace: true });
    },
    [navigate, queryNext],
  );

  React.useEffect(() => {
    setAuthState((prev) => {
      if (prev === 'verify' || prev === 'forgot' || prev === 'reset-sent') return prev;
      return initialAuthState;
    });
  }, [initialAuthState]);

  const trialEndedReason = searchParams.get('reason') === 'trial_ended';

  React.useEffect(() => {
    if (trialEndedReason) {
      setTrialEnded(true);
      setSessionChecked(true);
      return;
    }

    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        const instanceId = String(me?.activeInstance?.id ?? me?.instances?.[0]?.id ?? '').trim();
        if (me && instanceId) {
          goAfterLogin(instanceId);
          return;
        }
        if (getLastMeAuthFailureCode() === 'trial_ended') setTrialEnded(true);
        setSessionChecked(true);
      })
      .catch(() => {
        if (!cancelled) setSessionChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, [goAfterLogin, trialEndedReason]);

  const goToLogin = () => {
    navigate(withNext('/login'));
    setAuthState('login');
    setError(null);
  };

  const goToRegister = () => {
    navigate(withNext('/register'));
    setAuthState('register');
    setError(null);
    setOtpCode('');
  };

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = window.setTimeout(() => setResendCooldown((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const setGermanError = (err: unknown) => {
    const raw = err instanceof Error ? err.message : String(err);
    setError(toGerman(raw));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setTrialEnded(false);
    setLoading(true);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (authError) throw new Error(authError.message);
      const result = await exchangeTokenWithBackend(data.session.access_token);
      if (result.kind === 'pending_approval') {
        navigate('/pending-approval', { replace: true });
        return;
      }
      if (result.kind === 'trial_ended') {
        setTrialEnded(true);
        setError(result.message);
        return;
      }
      goAfterLogin(result.instanceId);

    } catch (err) {
      setGermanError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          full_name: fullName.trim(),
        }),
      });
      const payload = (await res.json().catch(() => null)) as { error?: string } | null;
      if (!res.ok) throw new Error(payload?.error ?? 'Registrierung fehlgeschlagen');
      setAuthState('verify');
      setOtpCode('');
      setResendCooldown(60);
    } catch (err) {
      setGermanError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const token = otpCode.trim();
      const normalizedEmail = email.trim();
      let data: Awaited<ReturnType<typeof supabase.auth.verifyOtp>>['data'] | null = null;
      let verifyError: Error | null = null;

      const signupAttempt = await supabase.auth.verifyOtp({
        email: normalizedEmail,
        token,
        type: 'signup',
      });
      if (!signupAttempt.error && signupAttempt.data.session) {
        data = signupAttempt.data;
      } else {
        const emailAttempt = await supabase.auth.verifyOtp({
          email: normalizedEmail,
          token,
          type: 'email',
        });
        if (emailAttempt.error) {
          verifyError = new Error(emailAttempt.error.message);
        } else {
          data = emailAttempt.data;
        }
      }

      if (verifyError) throw verifyError;
      if (!data?.session) throw new Error('Keine Session nach Bestätigung.');
      const result = await exchangeTokenWithBackend(data.session.access_token);
      if (result.kind === 'pending_approval') {
        navigate('/pending-approval', { replace: true });
        return;
      }
      if (result.kind === 'trial_ended') {
        setTrialEnded(true);
        goToLogin();
        setError(result.message);
        return;
      }
      goAfterLogin(result.instanceId);
    } catch (err) {
      setGermanError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError(null);
    try {
      const res = await fetch('/api/auth/resend-signup-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          full_name: fullName.trim(),
        }),
      });
      const payload = (await res.json().catch(() => null)) as { error?: string } | null;
      if (!res.ok) throw new Error(payload?.error ?? 'Code konnte nicht gesendet werden');
      setResendCooldown(60);
    } catch (err) {
      setGermanError(err);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const payload = (await res.json().catch(() => null)) as { error?: string } | null;
      if (!res.ok) throw new Error(payload?.error ?? 'Reset-Link konnte nicht gesendet werden');
      setAuthState('reset-sent');
    } catch (err) {
      setGermanError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    storeReturnPath(queryNext);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (oauthError) setError(toGerman(oauthError.message));
  };

  if (!sessionChecked) {
    return (
      <div className="theme-light min-h-screen flex items-center justify-center bg-pipedrive-bg text-slate-900">
        <PageLoadingState />
      </div>
    );
  }

  return (
    <AuthPageLayout>
          {authState === 'login' && (
            <>
              <div className="text-center lg:text-left">
                <h1 className="text-2xl font-bold text-slate-900">Willkommen zurück</h1>
                <p className="text-sm text-slate-500 mt-1">Melde dich an, um fortzufahren.</p>
              </div>

              {trialEnded ? (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                  <p className="font-semibold">Dein Testzeitraum ist abgelaufen</p>
                  <p className="mt-1">
                    Bitte melde dich unter{' '}
                    <a className="font-medium underline" href="mailto:hallo@meisterpfad.de">
                      hallo@meisterpfad.de
                    </a>
                    , um fortzufahren.
                  </p>
                </div>
              ) : null}

              <form onSubmit={(e) => { void handleLogin(e); }} className="mt-6 space-y-4">
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600 mb-1.5">E-Mail</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full border border-app-border rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3b82f6]/25 focus:border-[#3b82f6] transition-all"
                    placeholder="name@firma.de"
                  />
                </label>
                <label className="block">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-slate-600">Passwort</span>
                    <button
                      type="button"
                      onClick={() => { setAuthState('forgot'); setError(null); }}
                      className="text-xs text-[#3b82f6] hover:underline"
                    >
                      Passwort vergessen?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={passwordVisible ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="w-full border border-app-border rounded-xl px-3.5 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-[#3b82f6]/25 focus:border-[#3b82f6] transition-all"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                      aria-label={passwordVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                      title={passwordVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    >
                      {passwordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl bg-[#3b82f6] px-4 py-3 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-60 transition-colors shadow-sm"
                >
                  {loading ? 'Anmelden…' : 'Anmelden'}
                </button>
              </form>

              {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400">ODER</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <button
                type="button"
                onClick={() => { void handleGoogleLogin(); }}
                className="w-full inline-flex items-center justify-center gap-3 rounded-xl border border-app-border bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
              >
                <GoogleIcon />
                Mit Google anmelden
              </button>
            </>
          )}

          {/* ── REGISTER ── */}
          {authState === 'register' && (
            <>
              <div className="text-center lg:text-left">
                <h1 className="text-2xl font-bold text-slate-900">Konto erstellen</h1>
                <p className="text-sm text-slate-500 mt-1">Registriere dich kostenlos.</p>
              </div>

              <form onSubmit={(e) => { void handleRegister(e); }} className="mt-6 space-y-4">
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600 mb-1.5">Vollständiger Name</span>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    autoComplete="name"
                    className="w-full border border-app-border rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3b82f6]/25 focus:border-[#3b82f6] transition-all"
                    placeholder="Max Mustermann"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600 mb-1.5">E-Mail</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="w-full border border-app-border rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3b82f6]/25 focus:border-[#3b82f6] transition-all"
                    placeholder="name@firma.de"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600 mb-1.5">Passwort</span>
                  <div className="relative">
                    <input
                      type={passwordVisible ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      className="w-full border border-app-border rounded-xl px-3.5 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-[#3b82f6]/25 focus:border-[#3b82f6] transition-all"
                      placeholder="Mindestens 8 Zeichen"
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                      aria-label={passwordVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                      title={passwordVisible ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    >
                      {passwordVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl bg-[#3b82f6] px-4 py-3 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-60 transition-colors shadow-sm"
                >
                  {loading ? 'Konto erstellen…' : 'Konto erstellen'}
                </button>
              </form>

              {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}

              <p className="mt-4 text-center text-sm text-slate-500">
                Bereits registriert?{' '}
                <button
                  type="button"
                  onClick={goToLogin}
                  className="font-medium text-[#3b82f6] hover:underline"
                >
                  Anmelden
                </button>
              </p>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400">ODER</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <button
                type="button"
                onClick={() => { void handleGoogleLogin(); }}
                className="w-full inline-flex items-center justify-center gap-3 rounded-xl border border-app-border bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
              >
                <GoogleIcon />
                Mit Google registrieren
              </button>
            </>
          )}

          {/* ── FORGOT PASSWORD ── */}
          {authState === 'forgot' && (
            <>
              <div className="text-center lg:text-left">
                <h1 className="text-2xl font-bold text-slate-900">Passwort zurücksetzen</h1>
                <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                  Gib deine E-Mail-Adresse ein. Wir schicken dir einen Link zum Zurücksetzen.
                </p>
              </div>

              <form onSubmit={(e) => { void handleForgotPassword(e); }} className="mt-6 space-y-4">
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600 mb-1.5">E-Mail</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="email"
                    className="w-full border border-app-border rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3b82f6]/25 focus:border-[#3b82f6] transition-all"
                    placeholder="name@firma.de"
                  />
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl bg-[#3b82f6] px-4 py-3 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-60 transition-colors shadow-sm"
                >
                  {loading ? 'Link wird gesendet…' : 'Reset-Link senden'}
                </button>
              </form>

              {error ? (
                <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3.5 py-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              ) : null}

              <p className="mt-4 text-center text-sm text-slate-500">
                <button
                  type="button"
                  onClick={() => { setAuthState('login'); setError(null); }}
                  className="font-medium text-[#3b82f6] hover:underline"
                >
                  ← Zurück zum Login
                </button>
              </p>
            </>
          )}

          {/* ── RESET SENT ── */}
          {authState === 'reset-sent' && (
            <>
              <div className="flex flex-col items-center text-center gap-4">
                <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <svg className="w-7 h-7 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold text-slate-900">E-Mail gesendet</h1>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Wir haben einen Reset-Link an{' '}
                  <span className="font-medium text-slate-700">{email}</span> gesendet.
                  Bitte prüfe auch deinen Spam-Ordner.
                </p>
                <p className="text-xs text-slate-400">Der Link ist 1 Stunde gültig.</p>
              </div>

              <p className="mt-6 text-center text-sm text-slate-500">
                <button
                  type="button"
                  onClick={() => { setAuthState('login'); setError(null); }}
                  className="font-medium text-[#3b82f6] hover:underline"
                >
                  ← Zurück zum Login
                </button>
              </p>
            </>
          )}

          {/* ── VERIFY OTP ── */}
          {authState === 'verify' && (
            <>
              <div className="text-center lg:text-left">
                <h1 className="text-2xl font-bold text-slate-900">E-Mail bestätigen</h1>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                  Wir haben einen 6-stelligen Code an{' '}
                  <span className="font-medium text-slate-700">{email}</span> gesendet.
                  Bitte prüfe auch deinen Spam-Ordner.
                </p>
              </div>

              <form onSubmit={(e) => { void handleVerifyOtp(e); }} className="mt-6 space-y-4">
                <label className="block">
                  <span className="block text-xs font-medium text-slate-600 mb-1.5">Bestätigungscode</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    required
                    autoFocus
                    className="w-full border border-app-border rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3b82f6]/25 focus:border-[#3b82f6] transition-all tracking-[0.4em] text-center text-lg font-mono"
                    placeholder="123456"
                  />
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl bg-[#3b82f6] px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-600 disabled:opacity-60 transition-colors shadow-sm"
                >
                  {loading ? 'Bestätigen…' : 'Bestätigen'}
                </button>
              </form>

              {error ? (
                <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3.5 py-3">
                  <p className="text-sm text-red-600">{error}</p>
                  {(error.includes('abgelaufen') || error.includes('ungültig')) ? (
                    <button
                      type="button"
                      disabled={resendCooldown > 0}
                      onClick={() => { void handleResend(); }}
                      className="mt-1 text-sm font-medium text-red-700 hover:underline disabled:opacity-50"
                    >
                      {resendCooldown > 0 ? `Neuen Code senden (${resendCooldown}s)` : 'Neuen Code anfordern →'}
                    </button>
                  ) : null}
                </div>
              ) : null}

              <div className="mt-5 flex items-center justify-between text-sm">
                <button
                  type="button"
                  disabled={resendCooldown > 0}
                  onClick={() => { void handleResend(); }}
                  className="text-[#3b82f6] hover:underline disabled:opacity-50 disabled:no-underline"
                >
                  {resendCooldown > 0 ? `Erneut senden (${resendCooldown}s)` : 'Erneut senden'}
                </button>
                <button
                  type="button"
                  onClick={goToRegister}
                  className="text-slate-400 hover:text-slate-600 hover:underline"
                >
                  ← Zurück
                </button>
              </div>
            </>
          )}

    </AuthPageLayout>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}
